import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, collectionGroup, getDocs, getDoc, doc } from 'firebase/firestore';
import Layout from './Layout';
import { format, startOfTomorrow, endOfTomorrow, isWithinInterval } from 'date-fns';

const RemindersOverview = () => {
  const navigate = useNavigate();
  const [tomorrowReminders, setTomorrowReminders] = useState([]);
  const [erroredReminders, setErroredReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    setLoading(true);
    try {
      const allReminders = [];
      const tomorrowStart = startOfTomorrow();
      const tomorrowEnd = endOfTomorrow();
      
      // Get all users for mentor name lookup
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const userDetailsMap = new Map();
      
      for (const userDoc of usersSnapshot.docs) {
        const userId = userDoc.id;
        const userData = userDoc.data();
        userDetailsMap.set(userId, {
          name: userData.name || userData.email || userId,
          appID: userData.appID || userId,
        });
      }
      
      // Use collectionGroup to get ALL reminders, including orphaned ones
      // This finds reminders in users/{userId}/reminderQueue even if the user doc doesn't exist
      const reminderQueueGroup = collectionGroup(db, 'reminderQueue');
      const remindersSnapshot = await getDocs(reminderQueueGroup);
      
      remindersSnapshot.docs.forEach(reminderDoc => {
        const data = reminderDoc.data();
        
        // Extract userId from the document path: users/{userId}/reminderQueue/{reminderId}
        const pathParts = reminderDoc.ref.path.split('/');
        const userId = pathParts[1]; // users/{userId}/reminderQueue/{reminderId}
        
        const userDetails = userDetailsMap.get(userId) || {
          name: userId,
          appID: userId,
        };
        
        const scheduledFor = data.scheduledFor?.toDate ? data.scheduledFor.toDate() : null;
        
        allReminders.push({
          id: reminderDoc.id,
          userId: userId,
          mentorName: userDetails.name,
          mentorAppID: userDetails.appID,
          ...data,
          scheduledFor: scheduledFor,
          sentAt: data.sentAt?.toDate ? data.sentAt.toDate() : null,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null,
        });
      });
      
      // Filter tomorrow's reminders (scheduled for tomorrow, not archived, active status)
      const tomorrow = allReminders.filter(reminder => {
        if (reminder.archived === true) return false;
        if (!reminder.scheduledFor) return false;
        
        // Check if scheduled for tomorrow
        const isTomorrow = isWithinInterval(reminder.scheduledFor, {
          start: tomorrowStart,
          end: tomorrowEnd,
        });
        
        if (!isTomorrow) return false;
        
        // Only show active reminders (sent, snoozed, scheduled, queued)
        const activeStatuses = ['sent', 'snoozed', 'scheduled', 'queued'];
        return activeStatuses.includes(reminder.reminderStatus);
      });
      
      // Sort by scheduled time
      tomorrow.sort((a, b) => {
        if (!a.scheduledFor) return 1;
        if (!b.scheduledFor) return -1;
        return a.scheduledFor.getTime() - b.scheduledFor.getTime();
      });
      
      // Filter errored reminders (error status or failed queueStatus, not archived)
      const errored = allReminders.filter(reminder => {
        // Exclude archived reminders
        if (reminder.archived === true) return false;
        // Include reminders with error status or failed queueStatus
        return reminder.reminderStatus === 'error' || reminder.queueStatus === 'failed';
      });
      
      console.log('All reminders fetched:', allReminders.length);
      console.log('Errored reminders found:', errored.length);
      if (errored.length > 0) {
        console.log('Sample errored reminder:', errored[0]);
      }
      
      // Sort by creation date (most recent first)
      errored.sort((a, b) => {
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return b.createdAt.getTime() - a.createdAt.getTime();
      });
      
      setTomorrowReminders(tomorrow);
      setErroredReminders(errored);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    try {
      return format(date, 'MMM d, yyyy - h:mm a');
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Reminders Overview</h1>
            <p className="text-gray-600 mt-2">Tomorrow's reminders and errored reminders</p>
          </div>
          <button
            onClick={() => navigate('/reminder-queue-tester')}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Back to Reminder Queue Tester
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Tomorrow's Reminders Column */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">Tomorrow's Reminders</h2>
              <span className="text-sm text-gray-500 bg-blue-50 px-3 py-1 rounded">
                {tomorrowReminders.length} reminder{tomorrowReminders.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="space-y-3">
              {tomorrowReminders.length === 0 ? (
                <div className="p-4 bg-gray-50 rounded text-gray-500 text-center">
                  No reminders scheduled for tomorrow
                </div>
              ) : (
                tomorrowReminders.map(reminder => (
                  <div key={reminder.id} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-800 text-lg truncate">
                          {reminder.discipleName || 'Unknown Disciple'}
                        </h3>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Date & Time:</span>{' '}
                        <span className="text-gray-800">{formatDateTime(reminder.scheduledFor)}</span>
                      </div>
                      <div>
                        <span className="font-medium">Mentor:</span>{' '}
                        <span className="text-gray-800">{reminder.mentorName || reminder.mentorAppID || 'Unknown'}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          reminder.reminderStatus === 'sent' ? 'bg-blue-100 text-blue-800' :
                          reminder.reminderStatus === 'snoozed' ? 'bg-yellow-100 text-yellow-800' :
                          reminder.reminderStatus === 'scheduled' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {reminder.reminderStatus}
                        </span>
                        {reminder.queueStatus && (
                          <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">
                            {reminder.queueStatus}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Errored Reminders Column */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">Errored Reminders</h2>
              <span className="text-sm text-gray-500 bg-red-50 px-3 py-1 rounded">
                {erroredReminders.length} reminder{erroredReminders.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="space-y-3">
              {erroredReminders.length === 0 ? (
                <div className="p-4 bg-gray-50 rounded text-gray-500 text-center">
                  No errored reminders found
                </div>
              ) : (
                erroredReminders.map(reminder => (
                  <div key={reminder.id} className="border border-red-200 rounded-lg p-4 bg-red-50 shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-800 text-lg truncate">
                          {reminder.discipleName || 'Unknown Disciple'}
                        </h3>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Scheduled For:</span>{' '}
                        <span className="text-gray-800">{formatDateTime(reminder.scheduledFor)}</span>
                      </div>
                      <div>
                        <span className="font-medium">Mentor:</span>{' '}
                        <span className="text-gray-800">{reminder.mentorName || reminder.mentorAppID || 'Unknown'}</span>
                      </div>
                      <div>
                        <span className="font-medium">Created:</span>{' '}
                        <span className="text-gray-800">{formatDateTime(reminder.createdAt)}</span>
                      </div>
                      {reminder.sentAt && (
                        <div>
                          <span className="font-medium">Sent At:</span>{' '}
                          <span className="text-gray-800">{formatDateTime(reminder.sentAt)}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800">
                          {reminder.reminderStatus || 'error'}
                        </span>
                        {reminder.queueStatus && (
                          <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800">
                            {reminder.queueStatus}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RemindersOverview;
